import { motion } from 'framer-motion';
import { Colors } from '../../themes/colors';
import { Text } from '../Text';
import { Box } from '../Box';
import { Center } from '../Center';
import { ReactNode, useMemo } from 'react';

interface ITabProps {
  PrefixComponent?: ReactNode;
  title: string;
  badge?: string | number;
  isActive?: boolean;
  setActiveTab?: () => void;
  textStyle?: React.CSSProperties;
  totalCount?: number;
  totalCountStyle?: React.CSSProperties;
  totalCountTextStyle?: React.CSSProperties;
  isNew?: boolean;
  isVisible?: boolean;
  dataCy?: string;
  badgeCy?: string;
  variant?: 'border' | 'background';
}

const borderColorVariants = {
  active: {
    borderColor: Colors.secondary_500,
  },
  inactive: {
    borderColor: Colors.white,
  },
};

const backgroundColorVariants = {
  active: {
    backgroundColor: Colors.secondary_500,
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
  },
  inactive: {
    backgroundColor: 'transparent',
  },
};

const spanVariants = {
  active: {
    fontWeight: 700,
  },
  inactive: {
    fontWeight: 400,
  },
};

export const Tab = ({
  PrefixComponent,
  title,
  badge,
  isActive,
  setActiveTab,
  textStyle = {},
  totalCount,
  totalCountStyle = {},
  totalCountTextStyle = {},
  isNew = false,
  isVisible = true,
  dataCy,
  badgeCy,
  variant = 'border',
}: ITabProps) => {
  // Runtime prop validation - Pending Implementation
  if (variant === 'background' && (isNew || totalCount || PrefixComponent)) {
    throw new Error(
      // eslint-disable-next-line max-len
      `Tabs component: the combination of variant="background" with isNew or totalCount or PrefixComponent is not supported yet. Please use variant="border" instead.`,
    );
  }

  const activeColor = useMemo(() => {
    if (variant === 'background') {
      return Colors.white;
    }
    return Colors.neutral_600;
  }, [variant]);

  if (!isVisible) {
    return null;
  }

  return (
    <button
      key={isActive ? 'active' : 'inactive'}
      type="button"
      data-cy={dataCy}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setActiveTab?.();
      }}
      style={{
        all: 'unset',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {PrefixComponent && <Box marginBottom={3}>{PrefixComponent}</Box>}
        <motion.div
          initial="inactive"
          transition={{ duration: 0.3 }}
          animate={isActive ? 'active' : 'inactive'}
          variants={variant === 'background' ? backgroundColorVariants : borderColorVariants}
          style={
            variant === 'background'
              ? {
                  borderRadius: 8,
                  paddingInline: 12.5,
                  paddingBlock: 6.5,
                }
              : {
                  paddingRight: 10,
                  paddingLeft: 10,
                  borderBottomWidth: 3,
                  borderBottomStyle: 'solid',
                  position: 'relative',
                }
          }
        >
          <motion.span
            initial="inactive"
            transition={{ duration: 0.1 }}
            animate={isActive ? 'active' : 'inactive'}
            variants={spanVariants}
            style={{
              fontFamily: 'Nunito',
              display: 'flex',
              alignItems: 'center',
              color: isActive ? activeColor : Colors.neutral_300,
              fontSize: variant === 'background' ? 20 : 22,
              paddingBottom: variant === 'background' ? 0 : 4,
              ...textStyle,
            }}
          >
            {title}
            {!!badge && (
              <span
                data-cy={badgeCy}
                style={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Nunito',
                  backgroundColor: Colors.error_500,
                  color: Colors.white,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: 2,
                  minWidth: 16,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  top: -4,
                  right: -8,
                }}
              >
                {badge}
              </span>
            )}
          </motion.span>
        </motion.div>

        {totalCount != null && (
          <Box
            borderRadius={3}
            marginBottom={5}
            paddingHorizontal={7}
            paddingVertical={2}
            style={{
              backgroundColor: isActive ? Colors.primary_500 : Colors.primary_100,
              ...totalCountStyle,
            }}
          >
            <Text
              bold
              fontSize={14}
              style={{
                color: isActive ? Colors.white : Colors.primary_500,
                ...totalCountTextStyle,
              }}
            >
              {totalCount}
            </Text>
          </Box>
        )}

        {isNew && (
          <Center
            backgroundColor={Colors.secondary_500}
            width={48}
            height={20}
            borderRadius={3}
            marginBottom={8}
            marginLeft={8}
          >
            <Text bold fontSize={12} color="white">
              NEW
            </Text>
          </Center>
        )}
      </div>
    </button>
  );
};

Tab.displayName = 'Tab';
